import type { ComponentType, ReactNode } from 'react';
import { cn } from '../cn.js';
import { IconError, IconInfo, IconSuccess, IconWarning } from '../icons.js';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

export interface AlertProps {
  readonly tone?: AlertTone;
  readonly title: string;
  readonly children?: ReactNode;
  /** An action that answers the alert, where there is one. */
  readonly action?: ReactNode;
  readonly className?: string;
}

const byTone: Record<
  AlertTone,
  {
    icon: ComponentType<{ className?: string; 'aria-hidden'?: 'true' }>;
    surface: string;
    mark: string;
  }
> = {
  info: { icon: IconInfo, surface: 'bg-(--color-accent-subtle)', mark: 'text-(--color-accent)' },
  success: {
    icon: IconSuccess,
    surface: 'bg-(--color-success-subtle)',
    mark: 'text-(--color-success)',
  },
  warning: {
    icon: IconWarning,
    surface: 'bg-(--color-warning-subtle)',
    mark: 'text-(--color-warning)',
  },
  danger: { icon: IconError, surface: 'bg-(--color-danger-subtle)', mark: 'text-(--color-danger)' },
};

/**
 * A standing message about the thing a person is looking at.
 *
 * **Not a Toast**, which is about something that just happened and leaves; this stays, because
 * what it says is still true — a cluster is draining, a tenant has no plan, a trunk is
 * unreachable. **Not an ErrorState** either: that replaces content that could not load, where
 * this sits beside content that did.
 *
 * `danger` is announced; the other tones are not. An alert that interrupts a screen reader to say
 * something is fine is an alert that teaches people to ignore the next one. The icon is
 * decorative — the tone is carried by the words, because colour never carries meaning alone.
 */
export function Alert({ tone = 'info', title, children, action, className }: AlertProps) {
  const { icon: Icon, surface, mark } = byTone[tone];
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-(--space-sm) rounded-(--radius-md) p-(--space-md)',
        surface,
        className,
      )}
    >
      <Icon aria-hidden="true" className={cn('mt-px size-(--size-icon-md) shrink-0', mark)} />
      <div className="flex min-w-0 flex-1 flex-col gap-(--space-xs)">
        <p className="text-(length:--font-size-body) font-medium text-(--color-text-primary)">
          {title}
        </p>
        {children === undefined ? null : (
          <div className="text-(length:--font-size-body) text-(--color-text-secondary)">
            {children}
          </div>
        )}
      </div>
      {action === undefined ? null : <div className="shrink-0">{action}</div>}
    </div>
  );
}
